import type { QueryClient } from "@tanstack/react-query";
import type { StripboardTableDayBreak } from "components/dashboard/body/projects/panels/schedule/types";
import { scheduleKeys } from "queries";
import { formatBEDateToShortDateString, instanceOfStripboardTableDayBreak } from "utils";

interface ApiEnvelope<T> {
  data?: T;
}

const isDaybreakPayload = (value: unknown): value is StripboardTableDayBreak => {
  if (!value || typeof value !== "object") return false;
  return (
    "daybreak_id" in value && typeof (value as StripboardTableDayBreak).daybreak_id === "number"
  );
};

export const extractDaybreakPatchPayload = (response: unknown): StripboardTableDayBreak | null => {
  if (isDaybreakPayload(response)) {
    return response;
  }
  if (response && typeof response === "object" && "data" in response) {
    const data = (response as ApiEnvelope<unknown>).data;
    if (isDaybreakPayload(data)) {
      return data;
    }
  }
  return null;
};

const normalizeDaybreakDate = (date: string | null | undefined): string | null | undefined => {
  if (date == null) return date;
  return formatBEDateToShortDateString(date) || date;
};

export const mergeDaybreakInStripboardCache = (
  queryClient: QueryClient,
  projectId: string | number,
  documentId: string | number,
  updated: StripboardTableDayBreak
): void => {
  const normalizedDate = normalizeDaybreakDate(updated.date);
  const patch: StripboardTableDayBreak = {
    ...updated,
    ...(normalizedDate !== undefined ? { date: normalizedDate ?? undefined } : {}),
  };

  queryClient.setQueryData(scheduleKeys.stripboard(projectId, documentId), (old: unknown) => {
    if (!Array.isArray(old)) return old;
    return old.map((item) => {
      if (instanceOfStripboardTableDayBreak(item) && item.daybreak_id === patch.daybreak_id) {
        return { ...item, ...patch };
      }
      return item;
    });
  });
};
